import { useCallback } from "react";
import { SendChannel } from "@/entities/dreamerEmail/types";

// Builds the compose URL that opens the draft, pre-filled, in the chosen app.
// `recipientEmails` is a list of resolved recipient email addresses (see
// draftRecipientPersonIds/usePeople in DraftEmailCard) — recipients that could
// not be resolved to an email are already excluded by the caller.
// Bodies are plain text; note mailto has a practical length limit (Gmail/Outlook
// web compose tolerate long bodies). Exported for unit testing.
export const buildComposeUrl = (
  channel: SendChannel,
  subject: string,
  body: string,
  recipientEmails: string[] = []
): string => {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);
  const encodedTo = encodeURIComponent(recipientEmails.join(","));

  switch (channel) {
    case "gmail":
      return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedTo}&su=${encodedSubject}&body=${encodedBody}`;
    case "outlook":
      // Work/school (M365) host. Personal accounts use outlook.live.com — a
      // known limitation to revisit if personal-account users are in scope.
      return `https://outlook.office.com/mail/deeplink/compose?to=${encodedTo}&subject=${encodedSubject}&body=${encodedBody}`;
    case "mailto":
      // Addresses go in the addr-spec position, comma-separated. Each address
      // is percent-encoded individually — a local part can legally contain
      // URI-significant characters (?, #, %) that would otherwise corrupt
      // parsing of the query part that follows.
      return `mailto:${recipientEmails.map(encodeURIComponent).join(",")}?subject=${encodedSubject}&body=${encodedBody}`;
  }
};

export const useOpenDraftInEmailApp = () =>
  useCallback(
    (
      channel: SendChannel,
      subject: string,
      body: string,
      recipientEmails: string[] = []
    ) => {
      const url = buildComposeUrl(channel, subject, body, recipientEmails);
      if (channel === "mailto") {
        window.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    []
  );
